import {
  convert,
  newDate,
  getFields,
  extractKeys,
  strIndex
} from '@/sui/data/flatten/tools'

import {
  patientAge,
  addMO,
  findSurveillance,
  initializeSurveillance,
  addBacteremia,
  unitType
} from '@/sui/data/flatten/common'

function initializePatient (p, s, v, y) {
  const result = {
    type: 'diaInfADI',
    surv_id: p.sv
  }

  initializeSurveillance(result, s)

  result.sheet_id = p.id
  result.v = p.v
  result.st = p.st
  result.unit_type = unitType(y, p.f.unitType)
  result.uf = p.f.uf
  result.sheet_number = p.f.sheetNumber
  result.birthdate = newDate(p.f.birthdate)
  result.age = patientAge(p.f, y)
  result.sex = p.f.sex
  result.first_replacement = newDate(p.f.firstSubstn)
  result.hemodiafiltration_on_line = p.f.hemodiafiltr
  result.entry = newDate(p.f.entry)
  result.treated_diabetes = p.f.treatedDiab
  result.immuno = p.f.immuno
  result.hygiene = p.f.hygiene
  result.previous_sta_aur_infection = p.f.previousInfect
  result.previous_bacteremia = p.f.previousBact
  result.toxico = p.f.toxico
  result.vih = p.f.vih
  result.vhc = p.f.vhc
  result.height = p.f.height
  result.weight = p.f.weight
  result.ferritinaemia = p.f.ferritinaemia
  result.saturation = p.f.saturation
  result.hemoglobin = p.f.hemoglobin
  result.albumin = p.f.albumin
  result.creatinine = p.f.creatinine
  result.dialysis_count = p.f.dialNumber
  result.cardiovascular = p.f.cardioVascPb
  result.surgery = p.f.surgery
  result.transfusions_count = p.f.transfusions
  result.exit = newDate(p.f.exit)
  result.exit_reason = p.f.exitReason

  if (v >= 3) {
    result.covid = p.f.covidStatus
  }

  return result
}

function addInfection (prefix, target, inf, v) {
  const config = [
    { key: 'type', get: c => c.infection },
    { key: 'origin', get: c => c.origin },
    { key: 'episod', get: c => newDate(c.date) },
    { key: 'antibio', get: c => c.antibio }
  ]

  if (v >= 2) {
    config.push(
      { key: 'diag_date', get: c => newDate(c.diagDate) },
      { key: 'covid', get: c => c.covidStatus }
    )
  }

  getFields(config, prefix, target, inf)
  inf.microOrganisms.forEach(
    (mo, i) => addMO(
      `${prefix}_99_mo${strIndex(i + 1)}`, target, mo, false, v >= 4 ? 'v2' : 'v1'
    )
  )
}

function addKT (prefix, target, kt, v) {
  const as = v >= 2 ? c => c.accessSite_v2 : c => c.accessSite
  const config = [
    { key: 'site', get: as },
    { key: 'use', get: c => newDate(c.firstUse) },
    { key: 'uses', get: c => c.uses },
    { key: 'count', get: c => c.incidents },
    { key: 'unrelated_manip', get: c => c.unrelatedManip },
    { key: 'ablated', get: c => c.ablated },
    { key: 'ablation_date', get: c => newDate(c.ablation) },
    { key: 'use_ended', get: c => c.useEnded },
    { key: 'end_date', get: c => newDate(c.useEnding) },
    { key: 'install_reason', get: c => c.installReason },
    { key: 'tunneled', get: c => c.tunneled },
    { key: 'install_site', get: c => c.installSite },
    { key: 'site_change', get: c => c.siteChanges },
    { key: 'antibio_per_op', get: c => c.antibiopr }
  ]

  if (v >= 2) {
    config.push(
      { key: 'install_lock', get: c => c.installLock.lock },
      { key: 'install_lock_cap', get: c => c.installLock.cap },
      { key: 'disconnect_lock', get: c => c.disconnectLock.lock },
      { key: 'disconnect_lock_cap', get: c => c.disconnectLock.cap }
    )
  } else {
    config.push(
      { key: 'install_lock_heparine', get: c => c.installLock.heparine },
      { key: 'install_lock_citrate', get: c => c.installLock.citrate },
      { key: 'install_lock_antibio', get: c => c.installLock.antibio },
      { key: 'install_lock_phy', get: c => c.installLock.serumPhy },
      { key: 'install_lock_taurine', get: c => c.installLock.taurine },
      { key: 'install_lock_alcohol', get: c => c.installLock.alcohol },
      { key: 'install_lock_other', get: c => c.installLock.other },
      { key: 'install_lock_cap', get: c => c.installLock.cap },
      { key: 'disconnect_lock_heparine', get: c => c.disconnectLock.heparine },
      { key: 'disconnect_lock_citrate', get: c => c.disconnectLock.citrate },
      { key: 'disconnect_lock_antibio', get: c => c.disconnectLock.antibio },
      { key: 'disconnect_lock_phy', get: c => c.disconnectLock.serumPhy },
      { key: 'disconnect_lock_taurine', get: c => c.disconnectLock.taurine },
      { key: 'disconnect_lock_alcohol', get: c => c.disconnectLock.alcohol },
      { key: 'disconnect_lock_other', get: c => c.disconnectLock.other },
      { key: 'disconnect_lock_cap', get: c => c.disconnectLock.cap }
    )
  }

  getFields(config, prefix, target, kt)
  kt.infections.forEach(
    (inf, i) => addInfection(
      `${prefix}_98_inf${strIndex(i + 1)}`, target, inf, v
    )
  )
}

const reducer = (v, y) => (result, p) => {
  const s = findSurveillance(result, p)
  if (s != null && result.p.onlyClosed && s.status !== 'closed') {
    --result.r.length
    return result
  }

  const patient = initializePatient(p, s, v, y)
  if (result.keys == null) {
    result.keys = Object.keys(patient)
  }
  p.f.catheters.forEach(
    (c, i) => addKT(`kt${strIndex(i + 1)}`, patient, c, v)
  )
  p.f.bacteremias.forEach(
    (b, i) => addBacteremia(`bact${strIndex(i + 1)}`, patient, p.f, b, v)
  )
  extractKeys(result, patient, ['kt', 'bact'])
  result.r[result.i++] = patient
  return result
}

function run (v, y) {
  return (params) => convert(params, reducer(v, y), ['kt', 'bact'])
}

export default {
  // 2019 sheets are migrated to 2020
  '2019': run(2, 2019),
  '2020': run(2, 2020),
  '2021': run(3, 2021),
  '2022': run(3, 2022),
  '2023': run(4, 2023),
  '2024': run(4, 2024),
  '2025': run(4, 2025),
  '2026': run(4, 2026)
}
